import type { AutocompleteInputChangeReason, AutocompleteProps } from '@mui/material';
import coerceValue from 'app/common/utils/coerce-value';
import type { ImmutableMap } from 'app/common/utils/immutable-types';
import type { WidgetProps } from 'app/schema-form/types';
// biome-ignore lint/suspicious/noShadowRestrictedNames: Immutable.js Map/Set
import { type List, Map } from 'immutable';
import isPlainObject from 'lodash/isPlainObject';
import {
  type JSXElementConstructor,
  type MouseEventHandler,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useMemo
} from 'react';
import { getWidgetId, useWidgetTitle } from '../../../../schema-form/util/widget.js';
import { isImmutableRecord } from '../../../utils/immutable-type-util.js';
import type { FieldAlert } from '../../field-info/field-info.js';

export type AutocompleteOption = {
  label: string;
  value: string;
};

export type AutocompleteHookProps<Option = any> = {
  value: Option | string | string[] | Map<string, unknown> | List<unknown>;
  options?: Option[];
  isLoading?: boolean;
  startAdornment?: ReactNode;
  size?: 'small' | 'medium';
  onClear?: MouseEventHandler<HTMLButtonElement>;
  ListboxComponent?: JSXElementConstructor<any>;
  getOptionLabel?: (option: string | Option) => string;
  alert?: FieldAlert;
} & WidgetProps &
  Pick<
    AutocompleteProps<Option, false, false, false>,
    | 'size'
    | 'isOptionEqualToValue'
    | 'groupBy'
    | 'multiple'
    | 'readOnly'
    | 'onInputChange'
    | 'filterOptions'
    | 'renderOption'
  >;

export const useAutocomplete = <Option = any>(props: AutocompleteHookProps<Option>) => {
  const {
    onChange,
    onBlur,
    onInputChange,
    schema,
    config,
    getOptionLabel,
    disabled,
    readOnly,
    isRequired,
    isLoading,
    size,
    isOptionEqualToValue,
    ListboxComponent,
    groupBy,
    filterOptions,
    renderOption,
    startAdornment,
    error,
    alert
  } = props;
  const description = schema.get('description');
  const instructions = config?.get('instructions');
  const multiple = schema.get('type') === 'array';
  const freeSolo = config.get('freeSolo');
  const autoSelect = config.get('autoSelect');
  const label = useWidgetTitle(props);
  const id = getWidgetId(props);

  const options: Option[] = useMemo(() => {
    if (props.options) {
      return props.options;
    }

    const possibleOptions = config.get('options') ?? schema.get('enum') ?? [];

    if ((possibleOptions as Immutable.List<Record<string, string>>).toJS) {
      return possibleOptions.toJS();
    }

    return possibleOptions;
  }, [config, props.options, schema]);

  const value = useMemo(() => {
    const val = props.value;

    if (val === '') {
      if (multiple) {
        return [];
      }

      return '';
    }

    if (Array.isArray(val)) {
      if (multiple) {
        return val;
      }

      return val.join(',');
    }

    if (isImmutableRecord<any>(val)) {
      return val.toJS();
    }

    if (multiple) {
      return [val];
    }

    return val;
  }, [multiple, props.value]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, value: unknown) => {
      if (!onChange) {
        return;
      }

      if (isPlainObject(value)) {
        value = (value as AutocompleteOption).value;
      }

      onChange(coerceValue(schema, value));
      onBlur();
    },
    [onBlur, onChange, schema]
  );

  const handleInputChange = useCallback(
    (event: SyntheticEvent, value: unknown, reason: AutocompleteInputChangeReason) => {
      const changeOnInputChange = config.get('changeOnInputChange');

      if (Map.isMap(value)) {
        value = (value as ImmutableMap<any>).get('value');
      }

      if (changeOnInputChange) {
        onChange(coerceValue(schema, value));
      }

      if (onInputChange) {
        onInputChange(event, String(value), reason);
        onBlur();
      }
    },
    [config, onBlur, onChange, onInputChange, schema]
  );

  const defaultGetOptionLabel = useCallback(
    (op: string | Option) => {
      if (typeof op === 'string') {
        if (op === '') {
          return op;
        }

        if (!options) {
          return op;
        }

        const option = options.find((o) => {
          const oValue = (o as AutocompleteOption).value;

          if (oValue) {
            return oValue === op;
          }

          return false;
        });

        return (option as AutocompleteOption)?.label ?? op;
      }

      if (isPlainObject(op)) {
        return (op as AutocompleteOption).label;
      }

      return '';
    },
    [options]
  );

  return {
    id,
    label,
    value,
    description,
    instructions,
    multiple,
    freeSolo: Boolean(freeSolo),
    autoSelect: Boolean(autoSelect),
    options,
    disabled: Boolean(disabled) || Boolean(readOnly),
    isRequired,
    isLoading,
    size: size ?? 'small',
    handleChange,
    handleInputChange,
    getOptionLabel: getOptionLabel ?? defaultGetOptionLabel,
    isOptionEqualToValue,
    ListboxComponent,
    groupBy,
    filterOptions,
    renderOption,
    startAdornment,
    error,
    alert
  };
};
